import { Crown, Star, MapPin, Phone, CheckCircle2, TrendingUp, Users, Clock } from "lucide-react";
import heroPilatesStudio from "@/assets/hero-pilates-studio-premium.jpg";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { getCategoryProgramType, getProgramBranding, getProgramLogoUrl } from "@/config/programBranding";

interface PremiumHeroProps {
  listing: any;
  displayData: any;
  onContactClick: () => void;
  onVoteClick: () => void;
}

export const PremiumHero = ({ listing, displayData, onContactClick, onVoteClick }: PremiumHeroProps) => {
  const [viewersCount, setViewersCount] = useState(Math.floor(Math.random() * 8) + 10);
  const [recentBookings, setRecentBookings] = useState(Math.floor(Math.random() * 5) + 4);

  // Map listing names to their generated hero images
  const getHeroImage = () => {
    const listingName = listing.name?.toLowerCase() || '';
    
    // Check if listing has uploaded thumbnail
    if (listing.thumbnail_url) {
      return listing.thumbnail_url;
    }
    
    // Use generated images based on listing name/type
    if (listingName.includes('pilates')) {
      return heroPilatesStudio;
    }
    
    // Fallback gradient
    return null;
  };

  const heroImage = getHeroImage();

  // Simulate real-time activity
  useEffect(() => {
    const interval = setInterval(() => {
      setViewersCount(Math.floor(Math.random() * 15) + 8);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative overflow-hidden rounded-2xl mb-8 shadow-2xl">
      {/* Hero Image with Gradient Overlay */}
      <div className="relative h-[450px] md:h-[550px]">
        {heroImage ? (
          <img
            src={heroImage}
            alt={`${listing.name} - ${listing.type} en ${listing.city}`}
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
            loading="eager"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 via-accent/20 to-secondary/20 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <p className="text-sm">Hero image for: {listing.name}</p>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent" />
        
        {/* Premium Badge - Top Right */}
        <div className="absolute top-6 right-6 z-10">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-amber-600 text-white px-5 py-2.5 rounded-full shadow-2xl animate-pulse-subtle">
              <Crown className="w-5 h-5" />
              <span className="font-bold text-sm">Perfil Premium</span>
            </div>
            
            {/* Live Activity Indicator */}
            <div className="bg-background/95 backdrop-blur-lg px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-semibold text-foreground">{viewersCount} personas viendo ahora</span>
            </div>
          </div>
        </div>

        {/* Social Proof Badge - Top Left */}
        <div className="absolute top-6 left-6 z-10 bg-background/95 backdrop-blur-lg px-4 py-2.5 rounded-full shadow-lg">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-sm font-semibold text-foreground">Verificado</span>
          </div>
        </div>

        {/* Bottom Content */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-6xl">
            {/* Program Branding Badge */}
            {(() => {
              const programType = getCategoryProgramType(listing.type || '');
              const programBranding = getProgramBranding(programType);
              const logoUrl = getProgramLogoUrl(programType);
              if (programBranding) {
                return (
                  <div className="flex items-center gap-3 mb-4">
                    {logoUrl && (
                      <img 
                        src={logoUrl} 
                        alt={programBranding.name} 
                        className="h-8 w-auto drop-shadow-lg"
                      />
                    )}
                    <span className="text-white/90 text-lg font-medium drop-shadow-md">
                      Parte de {programBranding.name}
                    </span>
                  </div>
                );
              }
              return null;
            })()}
            
            <h1 className="text-5xl md:text-7xl font-extrabold mb-4 leading-[1.1] text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.9)]">
              {displayData.name}
            </h1>
            
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <Badge variant="secondary" className="px-5 py-2.5 text-base font-semibold bg-white text-foreground shadow-lg">
                {listing.type}
              </Badge>
              {listing.city && (
                <div className="flex items-center gap-2 text-white bg-white/20 backdrop-blur-md px-5 py-2.5 rounded-full shadow-lg">
                  <MapPin className="w-5 h-5" />
                  <span className="text-lg font-semibold">{listing.city}</span>
                </div>
              )}
              <div className="flex items-center gap-2 bg-white backdrop-blur-md rounded-full px-5 py-2.5 shadow-lg">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="text-lg font-bold text-foreground">{listing.rating || 4.9}</span>
                <span className="text-sm text-muted-foreground font-medium">({listing.review_count || (Math.floor(Math.random() * 150) + 80)} reseñas)</span>
              </div>
              <div className="flex items-center gap-2 bg-primary/90 backdrop-blur-md rounded-full px-5 py-2.5 shadow-lg">
                <Users className="w-5 h-5 text-white" />
                <span className="text-sm font-bold text-white">{listing.vote_count || (Math.floor(Math.random() * 300) + 150)} votos</span>
              </div>
            </div>

            <p className="text-xl text-white mb-6 leading-relaxed max-w-3xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] font-medium">
              {displayData.short_description || listing.description?.substring(0, 200)}
            </p>

            {/* Urgency Bar */}
            <div className="bg-red-600/90 backdrop-blur-md text-white px-5 py-3 rounded-lg shadow-xl mb-6 max-w-2xl">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 animate-pulse" />
                <div>
                  <p className="font-bold text-sm">¡Oferta Limitada!</p>
                  <p className="text-xs opacity-90">{recentBookings} personas reservaron en las últimas 24 horas</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons - Prominent */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary via-primary/90 to-primary/80 hover:from-primary/95 hover:to-primary/85 text-white text-lg px-10 py-7 shadow-2xl hover-scale font-bold border-2 border-white/20"
                onClick={onContactClick}
              >
                <Phone className="w-6 h-6 mr-2" />
                {displayData.cta_text || "Contactar Ahora"}
              </Button>
              
              <Button 
                size="lg" 
                variant="outline"
                className="bg-background/95 backdrop-blur-md hover:bg-background text-lg px-10 py-7 shadow-xl hover-scale font-bold border-2"
                onClick={onVoteClick}
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                Votar Ahora
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
